import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';

import type { OfferBuilderRoom } from './types';

export { getRoomKey } from '../utils';

export function getRoomImageCount(room: PropertyRoomTypeWithImages) {
  return (room.images ?? []).filter((image) => image.id_cloudinary_external).length;
}

export function getRoomSummary(room: OfferBuilderRoom) {
  const summaryParts = [
    formatBedSummary(room),
    typeof room.size_sqm === 'number' && room.size_sqm > 0 ? `${room.size_sqm}m²` : undefined,
  ].filter(Boolean);

  return summaryParts.length ? summaryParts.join(' · ') : 'Room details missing';
}

export function formatBedType(type?: string) {
  return (type ?? '')
    .replace(/Bed$/, '')
    .replace('DoubleSofa', 'Double Sofa')
    .replace('QueenSofa', 'Queen Sofa')
    .replace('RollAway', 'Roll Away')
    .trim();
}

function formatBedConfiguration(configuration: { quantity?: number; size?: string; type?: string }) {
  if (!configuration.quantity) {
    return undefined;
  }

  const bedType = formatBedType(configuration.type);
  const bedSize = formatBedType(configuration.size);
  const bedLabel = bedType || bedSize;

  return bedLabel ? `${configuration.quantity} ${bedLabel} Bed${configuration.quantity > 1 ? 's' : ''}` : undefined;
}

function formatBedSummary(room: OfferBuilderRoom) {
  const bedConfigurations = room.bed_groups?.flatMap((group) => group.configuration ?? []) ?? [];
  const bedConfigurationSummaries = bedConfigurations.map(formatBedConfiguration).filter(Boolean);

  if (bedConfigurationSummaries.length > 0) {
    const lastBedConfigurationSummary = bedConfigurationSummaries[bedConfigurationSummaries.length - 1];
    const leadingBedConfigurationSummaries = bedConfigurationSummaries.slice(0, -1);

    return leadingBedConfigurationSummaries.length
      ? `${leadingBedConfigurationSummaries.join(', ')} and ${lastBedConfigurationSummary}`
      : lastBedConfigurationSummary;
  }

  if (typeof room.number_of_beds === 'number') {
    return `${room.number_of_beds} ${room.number_of_beds === 1 ? 'bed' : 'beds'}`;
  }

  if (Array.isArray(room.number_of_beds) && room.number_of_beds.length > 0) {
    const bedCount = room.number_of_beds.reduce((total, count) => total + count, 0);
    return `${bedCount} ${bedCount === 1 ? 'bed' : 'beds'}`;
  }

  return undefined;
}
